// Import necessary types and hooks
import { UseKycNavigationProps } from './props';
import { useCallback, useMemo } from 'react';
import { useCurrentModal } from '../../modals/useCurrentModal';
import { useUserOriginId } from '../../user-identity/useUserOriginId';
import { KycCheckpointId, CommonModalsKey } from '../../../enums';
import { Nullable } from '../../../interfaces';
import { useKycMeByOriginId } from '../../../api';
import { KycCheckpointMapping } from '../../../models/kyc/checkpoint/mapping';
import { StepProps } from '../../../components/Steps/props';
import { KycCheckpointStatus } from '../../../enums/kyc/checkpoint/status';
import { CreateDepositRequestDto } from '../../../dto';
import { useSearchParams } from '../../useSearchParams';
import { useNavigate } from '../../useNavigate';
import { NavigateFunction } from '@starsoft/common/interfaces/navigate-function';
import { KycCheckpointIdType } from '@starsoft/common/enums/kyc/checkpoint';
import { parseToBigInt } from '@starsoft/common/utils/parsers';

/**
 * Custom hook to manage KYC navigation.
 * This hook provides functionality to navigate through KYC checkpoints and manage related state.
 *
 * @template T - The type of the callback data.
 * @param {UseKycNavigationProps<T>} props - The properties required for KYC navigation.
 * @returns {Object} - An object containing various handlers and state related to KYC navigation.
 *
 * Example usage:
 * const { handleNextKycStep, handleKycDepositStep } = useKycNavigation<MyDataType>({
 *   callback: myCallbackFunction,
 *   externalOriginId: '123',
 *   externalEnabled: true,
 * });
 */
export function useKycNavigation<T>({
  callback,
  externalOriginId,
  externalEnabled,
}: UseKycNavigationProps<T>) {
  // Hook to manage URL search parameters
  const search: URLSearchParams = useSearchParams();
  // Hook to navigate between routes
  const navigate: NavigateFunction = useNavigate();
  // Hook to get the current modal key
  const modal: string | undefined = useCurrentModal(CommonModalsKey);

  // Get the user's origin ID, falling back to externalOriginId if not available
  const identityOrigin: Nullable<string> =
    useUserOriginId() ?? externalOriginId?.toString();
  // Fetch KYC information based on the origin ID
  const kycMeByOriginIdQuery = useKycMeByOriginId({
    originId: identityOrigin?.toString(),
    enabled: externalEnabled,
  });
  // Check if there is a KYC check in the current identity
  const hasKycCheckInCurrentIdentity: boolean = !!identityOrigin;
  // Get the KYC checkpoint ID from the search parameters
  const kycCheckpointIdSearch: Nullable<string> =
    search.get('kyc-checkpoint-id');
  // Determine the current KYC checkpoint ID
  const kycCheckpointId: Nullable<bigint> =
    (kycCheckpointIdSearch
      ? (parseToBigInt(kycCheckpointIdSearch) as bigint)
      : null) ??
    kycMeByOriginIdQuery?.kyc?.stage?.checkpoint?.id ??
    KycCheckpointId.Identity;
  // Memoize the checkpoints from the KYC query
  const checkpoints: KycCheckpointMapping[] = useMemo(
    () => kycMeByOriginIdQuery?.kyc?.tracking ?? [],
    [kycMeByOriginIdQuery?.kyc?.tracking],
  );
  // Check if the current KYC checkpoint ID is valid
  const isValidKycCheckpointId: boolean = useMemo(
    () =>
      checkpoints?.some(
        tracking => tracking?.checkpoint?.id === kycCheckpointId,
      ),
    [checkpoints, kycCheckpointId],
  );

  // Determine the index of the current step
  const currentStepIndex: number = useMemo(
    () =>
      checkpoints?.findIndex(
        checkpoint => checkpoint?.checkpoint?.id == kycCheckpointId,
      ),
    [kycCheckpointId, checkpoints],
  );
  // Parse the steps for the KYC process
  const parsedSteps: StepProps<KycCheckpointIdType>[] = useMemo(
    () =>
      checkpoints?.map(checkpoint => ({
        label: `${checkpoint?.checkpoint?.id}_kyc_step`,
        value: checkpoint?.checkpoint?.id as KycCheckpointIdType,
        isCompleted: checkpoint?.status === KycCheckpointStatus.Verified,
      })),
    [checkpoints],
  );

  // Check if the current step is the last step
  const isLastStep: boolean =
    kycMeByOriginIdQuery?.kyc?.checkpoints?.length - 1 == currentStepIndex;

  /**
   * Function to handle navigation to the next KYC step.
   * If the current step is the last step, it triggers the callback.
   */
  const handleNextKycStep = useCallback(() => {
    if (isLastStep) {
      callback?.();
      return;
    }

    if (!checkpoints?.[currentStepIndex + 1]?.checkpoint?.id) {
      return;
    }

    search.set(
      'kyc-checkpoint-id',
      `${checkpoints?.[currentStepIndex + 1]?.checkpoint?.id}`,
    );
    navigate({
      search: search.toString(),
      hash: modal,
    });
  }, [
    modal,
    search,
    navigate,
    currentStepIndex,
    callback,
    isLastStep,
    checkpoints,
  ]);

  /**
   * Function to set the KYC step based on the given checkpoint.
   *
   * @param {KycCheckpointIdType} checkpoint - The checkpoint ID to set.
   */
  const handleSetKycStep = useCallback(
    (checkpoint: KycCheckpointIdType) => {
      search.set('kyc-checkpoint-id', `${checkpoint?.toString()}`);
      navigate({
        search: search.toString(),
        hash: modal,
      });
    },
    [modal, search, navigate],
  );

  /**
   * Function to set both KYC and deposit step search parameters.
   *
   * @param {KycCheckpointIdType} checkpoint - The checkpoint ID to set.
   * @param {T} depositStep - The deposit step to set.
   */
  const handleSetKycAndStepSearchParams = useCallback(
    (checkpoint: KycCheckpointIdType, depositStep: T) => {
      search.set('kyc-checkpoint-id', `${checkpoint?.toString()}`);
      search.set('step', String(depositStep));
      navigate({
        search: search.toString(),
        hash: modal,
      });
    },
    [modal, search, navigate],
  );

  /**
   * Function to handle the KYC deposit step.
   * If the current step is the last step, it triggers the callback with the provided data.
   *
   * @param {CreateDepositRequestDto} [data] - Optional data for the deposit request.
   */
  const handleKycDepositStep = useCallback(
    async (data?: CreateDepositRequestDto) => {
      if (isLastStep) {
        await callback?.(data as T);
        return;
      }

      handleNextKycStep();
    },
    [isLastStep, callback, handleNextKycStep],
  );

  // Return the handlers and state related to KYC navigation
  return {
    handleNextKycStep,
    handleKycDepositStep,
    isLastStep,
    identityOrigin,
    kycCheckpointId,
    currentStepIndex,
    handleSetKycStep,
    handleSetKycAndDepositStep: handleSetKycAndStepSearchParams,
    handleSetKycAndStepSearchParams,
    kycMeByOriginIdQuery,
    parsedSteps,
    hasKycCheckInCurrentIdentity,
    currentCheckpoint: checkpoints?.[currentStepIndex],
    checkpoints,
    isValidKycCheckpointId,
    kycCheckpointIdSearch,
  };
}

// Export the type for the hook's props
export type { UseKycNavigationProps };
